import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';

export interface PyramidCategory {
  uuid: string;
  title: string;
  score: number;
  icon: string;
  color: string;
  gradientColor: string;
  trend: 'up' | 'down' | null;
}

export interface PyramidLegendItem {
  label: string;
  color: string;
}

interface PyramidBand {
  category: PyramidCategory;
  points: string;
  chipLeft: number;
  chipTop: number;
  chipSize: number;
  iconSize: number;
  valueTop: number;
}

@Component({
  selector: 'app-categories-pyramid-display',
  templateUrl: './categories-pyramid-display.component.html',
  styleUrls: ['./categories-pyramid-display.component.scss']
})
export class CategoriesPyramidDisplayComponent implements OnInit {

  private static readonly APEX_X = 150;
  private static readonly APEX_Y = 18;
  private static readonly BASE_Y = 300;
  private static readonly HALF_TOP = 25;
  private static readonly HALF_BASE = 142;
  private static readonly HEIGHT = CategoriesPyramidDisplayComponent.BASE_Y - CategoriesPyramidDisplayComponent.APEX_Y;
  private static readonly BAND_GAP = 1.5;
  private static readonly CHIP_SIZE = 42;
  private static readonly CHIP_MIN_SPACING = 4;
  private static readonly ICON_SIZE = 20;
  private static readonly CHIP_FIRST_LEFT = 201;
  private static readonly CHIP_LAST_LEFT = 294;
  private static readonly VALUE_HEIGHT = 29;

  bands: PyramidBand[] = [];

  @Input() set categories(value: PyramidCategory[]) {
    this.pyramidCategories = value ?? [];
    this.buildBands();
  }

  get categories(): PyramidCategory[] {
    return this.pyramidCategories;
  }

  @Input() legend: PyramidLegendItem[] = [];
  @Input() showLegend: boolean = true;

  @Output() categorySelected = new EventEmitter<PyramidCategory>();

  private pyramidCategories: PyramidCategory[] = [];

  constructor() {
  }

  ngOnInit(): void {
  }

  selectCategory(category: PyramidCategory) {
    this.categorySelected.emit(category);
  }

  trackByUuid(index: number, band: PyramidBand) {
    return band.category.uuid ?? index;
  }

  private buildBands() {
    const total = this.pyramidCategories.length;
    if (!total) {
      this.bands = [];
      return;
    }
    const bandHeight = CategoriesPyramidDisplayComponent.HEIGHT / total;
    const chipSize = Math.min(
      CategoriesPyramidDisplayComponent.CHIP_SIZE,
      bandHeight - CategoriesPyramidDisplayComponent.CHIP_MIN_SPACING
    );
    const iconSize = chipSize * (CategoriesPyramidDisplayComponent.ICON_SIZE /
      CategoriesPyramidDisplayComponent.CHIP_SIZE);

    this.bands = this.pyramidCategories.map((category, index) => {
      const top = CategoriesPyramidDisplayComponent.APEX_Y + (index * bandHeight);
      const bottom = top + bandHeight;
      return {
        category,
        points: this.getBandPoints(top, bottom, index === 0, index === total - 1),
        chipLeft: this.round(this.getChipLeft(index, total)),
        chipTop: this.round(top + ((bandHeight - chipSize) / 2)),
        chipSize: this.round(chipSize),
        iconSize: this.round(iconSize),
        valueTop: this.round(top + ((bandHeight - CategoriesPyramidDisplayComponent.VALUE_HEIGHT) / 2))
      };
    });
  }

  private getBandPoints(top: number, bottom: number, isFirst: boolean, isLast: boolean): string {
    const gap = CategoriesPyramidDisplayComponent.BAND_GAP;
    const topY = isFirst ? top : top + gap;
    const bottomY = isLast ? bottom : bottom - gap;
    const apexX = CategoriesPyramidDisplayComponent.APEX_X;
    const topHalf = this.getHalfWidth(topY);
    const bottomHalf = this.getHalfWidth(bottomY);

    return `${this.round(apexX - topHalf)},${this.round(topY)} ` +
      `${this.round(apexX + topHalf)},${this.round(topY)} ` +
      `${this.round(apexX + bottomHalf)},${this.round(bottomY)} ` +
      `${this.round(apexX - bottomHalf)},${this.round(bottomY)}`;
  }

  private round(value: number): number {
    return Math.round(value * 100) / 100;
  }

  private getHalfWidth(y: number): number {
    const halfTop = CategoriesPyramidDisplayComponent.HALF_TOP;
    return halfTop + ((CategoriesPyramidDisplayComponent.HALF_BASE - halfTop) *
      ((y - CategoriesPyramidDisplayComponent.APEX_Y) / CategoriesPyramidDisplayComponent.HEIGHT));
  }

  private getChipLeft(index: number, total: number): number {
    if (total < 2) {
      return CategoriesPyramidDisplayComponent.CHIP_FIRST_LEFT;
    }
    const span = CategoriesPyramidDisplayComponent.CHIP_LAST_LEFT - CategoriesPyramidDisplayComponent.CHIP_FIRST_LEFT;
    return CategoriesPyramidDisplayComponent.CHIP_FIRST_LEFT + ((span * index) / (total - 1));
  }
}
